import React, { createContext, ReactElement, useContext } from 'react';
import { Option } from 'fp-ts/lib/Option';
import { useCase, CriteriaCallback } from 'adapter/in/common/hooks/useCase';
import { Features } from 'domain/entity/feature';
import { ReadFeatures } from 'use-case/features/read/readFeaturesUseCase';
import { ErrorType } from 'domain/types';
import Preset from 'domain/entity/preset';

type FeaturesReadType = {
    read: CriteriaCallback;
    readIsLoading: boolean;
    readResult: Option<Features>;
    readError: ErrorType;
    readReset?: () => void;
};

export const findAvailableWidgetTypes = (presetKey: string, featureList: Features) =>
    featureList
        .map((feature) => feature.presets)
        .reduce((previous, current) => previous.concat(current))
        .find((preset: Preset) => preset.key === presetKey)?.widgetTypes ?? [];

export const FeaturesContext = createContext({} as FeaturesReadType);
export const useFeatures = (): FeaturesReadType => useContext(FeaturesContext);

export type FeaturesProviderType = {
    readFeatures: ReadFeatures;
    children: ReactElement;
};

export const FeaturesProvider = (props: FeaturesProviderType): ReactElement => {
    const { children, readFeatures } = props;

    const { isLoading: readIsLoading, data: readResult, error: readError, execute: read } = useCase(readFeatures, false);

    return (
        <FeaturesContext.Provider
            value={{
                readIsLoading,
                readResult,
                readError,
                read,
            }}
        >
            {children}
        </FeaturesContext.Provider>
    );
};
